/**
 * The only player-details form in this repo.
 *
 * It owns the roster fetch, the player picker, jersey prefill, both handle
 * fields with @-stripping, validation, the empty-roster and failed-lookup
 * states, the mobile bottom sheet and the copy. Surfaces that need a variation
 * pass a prop (see `allowFreeTextPlayer`, `requireHandles`); they do not fork.
 *
 * Admin surfaces that edit an existing purchase embed <PlayerDetailsFields />
 * from this file rather than re-declaring the inputs.
 */
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useIsMobile } from "@/hooks/use-mobile";
import { Loader2 } from "lucide-react";
import { cleanHandle, cleanHandleInput, isValidHandle } from "@/lib/handles";

export interface PlayerDetails {
  player_name?: string;
  player_jersey_number?: string;
  player_instagram_handle?: string;
  player_x_handle?: string;
  /** Manual entry only: who the package belongs to and where it's delivered. */
  team_name?: string;
  buyer_email?: string;
}

const isEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim());

const money = (n: number) => `$${(Number(n) || 0).toFixed(2)}`;

/* ───── The roster fetch, once ─────
   Public endpoint: the buyer is often NOT this team's coach, so scoping to the
   signed-in user's registrations reports "no roster" for teams that have one.
   Names arrive pre-abbreviated server-side. */
export function useEventRosterPlayers(
  eventId?: string | null,
  registrationId?: string | null,
  enabled = true
) {
  const q = useQuery({
    queryKey: ["ee-roster", registrationId],
    enabled: !!registrationId && enabled,
    queryFn: async () => {
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/rle-roster-players-public`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({ event_id: eventId, registration_id: registrationId }),
        }
      );
      const json = await res.json().catch(() => null);
      // ok:false is a lookup failure, not an empty roster — keep them distinct.
      return { ok: !!json?.ok, players: json?.ok ? json.players ?? [] : null };
    },
  });
  return {
    roster: (q.data?.players ?? []) as any[],
    loadingRoster: q.isLoading,
    rosterFailed: !!q.data && !q.data.ok,
  };
}

/* ───── The fields themselves ───── */
export function PlayerDetailsFields({
  name,
  jersey,
  ig,
  x,
  teamName = "",
  email = "",
  onChange,
  roster,
  loadingRoster,
  rosterFailed,
  allowFreeTextPlayer,
  disabled,
  idPrefix = "pe",
  requiresPlayer = true,
  requireHandles = true,
  manual = false,
  onManual,
  collectContact = false,
}: {
  name: string;
  jersey: string;
  ig: string;
  x: string;
  teamName?: string;
  email?: string;
  onChange: (patch: {
    name?: string; jersey?: string; ig?: string; x?: string;
    teamName?: string; email?: string;
  }) => void;
  roster?: any[];
  loadingRoster?: boolean;
  rosterFailed?: boolean;
  /** Pre-registration surfaces have no roster to pick from yet. */
  allowFreeTextPlayer?: boolean;
  disabled?: boolean;
  idPrefix?: string;
  /** Show and require player name + jersey. */
  requiresPlayer?: boolean;
  /** Show and require Instagram + X handles. */
  requireHandles?: boolean;
  /** Type the player rather than pick them off a roster. */
  manual?: boolean;
  /** Offer the switch to manual entry; omit where there is nothing to switch from. */
  onManual?: () => void;
  /** Manual entry also collects the team name and a delivery email. */
  collectContact?: boolean;
}) {
  const list = roster ?? [];
  const freeText = manual || !!allowFreeTextPlayer;

  const pickPlayer = (label: string) => {
    const p = list.find((r: any) => r.label === label);
    onChange({
      name: label,
      // Prefill from the roster, but leave it correctable.
      jersey: p?.jersey_number != null && String(p.jersey_number) !== "" ? String(p.jersey_number) : jersey,
    });
  };

  const handleField = (id: string, label: string, value: string, key: "ig" | "x") => (
    <div className="space-y-1.5">
      <Label htmlFor={id}>
        {label} <span className="text-destructive">*</span>
      </Label>
      <div className="flex items-center h-11 rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
        <span className="pl-3 pr-1 text-muted-foreground select-none">@</span>
        <Input
          id={id}
          disabled={disabled}
          className="border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 pl-0 h-11 text-base"
          placeholder="handle"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          value={value}
          onChange={(e) => onChange({ [key]: cleanHandleInput(e.target.value) } as any)}
        />
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {requiresPlayer && (
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-player`}>
            Player <span className="text-destructive">*</span>
          </Label>
          {/* Roster picking is the default where a roster exists — it is faster
              and gets the spelling right. Where there is no roster (a signed-out
              parent, a team with nothing uploaded, a failed lookup) the buyer
              types the player in: an empty dropdown is a dead end, not a guard. */}
          {freeText ? (
            <Input
              id={`${idPrefix}-player`}
              disabled={disabled}
              className="h-11 text-base"
              placeholder="Full name"
              value={name}
              onChange={(e) => onChange({ name: e.target.value })}
            />
          ) : loadingRoster ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground h-11">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading roster…
            </div>
          ) : (
            <Select value={name} onValueChange={pickPlayer} disabled={disabled}>
              <SelectTrigger id={`${idPrefix}-player`} className="h-11 text-base">
                <SelectValue placeholder="Choose the player" />
              </SelectTrigger>
              <SelectContent className="max-h-[50vh]">
                {list.map((p: any) => (
                  <SelectItem key={p.key} value={p.label} className="py-3 text-base">
                    {p.jersey_number ? `#${p.jersey_number} · ` : ""}{p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {!freeText && onManual && (
            <Button
              type="button"
              variant="link"
              className="h-auto p-0 text-xs"
              onClick={onManual}
              disabled={disabled}
            >
              Player not listed? Enter manually
            </Button>
          )}
          {freeText && rosterFailed && (
            <p className="text-xs text-muted-foreground">
              We couldn't load this team's roster — type the player's details in.
            </p>
          )}
        </div>
      )}

      {requiresPlayer && (
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-jersey`}>
            Jersey number <span className="text-destructive">*</span>
          </Label>
          <Input
            id={`${idPrefix}-jersey`}
            disabled={disabled}
            inputMode="numeric"
            className="h-11 text-base"
            placeholder="12"
            value={jersey}
            onChange={(e) => onChange({ jersey: e.target.value })}
          />
        </div>
      )}

      {requiresPlayer && collectContact && (
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-team`}>
            Team name <span className="text-destructive">*</span>
          </Label>
          <Input
            id={`${idPrefix}-team`}
            disabled={disabled}
            className="h-11 text-base"
            placeholder="e.g. Michigan Elite 16U"
            value={teamName}
            onChange={(e) => onChange({ teamName: e.target.value })}
          />
          <p className="text-xs text-muted-foreground">
            The media team uses this to place the player at the event.
          </p>
        </div>
      )}

      {requireHandles && handleField(`${idPrefix}-ig`, "Instagram", ig, "ig")}
      {requireHandles && handleField(`${idPrefix}-x`, "X / Twitter", x, "x")}

      {requiresPlayer && collectContact && (
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-email`}>
            Email <span className="text-destructive">*</span>
          </Label>
          <Input
            id={`${idPrefix}-email`}
            disabled={disabled}
            type="email"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            className="h-11 text-base"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => onChange({ email: e.target.value })}
          />
          <p className="text-xs text-muted-foreground">
            Where the finished package and receipt are sent.
          </p>
        </div>
      )}
    </div>
  );
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  extra: any | null;
  teamLabel: string;
  /** Pass a roster, or an eventId + registrationId and the dialog fetches it. */
  roster?: any[];
  loadingRoster?: boolean;
  rosterFailed?: boolean;
  eventId?: string | null;
  registrationId?: string | null;
  /** Pre-registration: the team doesn't exist yet, so the player is typed. */
  allowFreeTextPlayer?: boolean;
  /** Handles are required where the product is social media delivery. */
  requireHandles?: boolean;
  /** Manual entry collects team name and delivery email too. */
  collectContact?: boolean;
  /** Prefills for the manual-entry team name / email. */
  defaultTeamName?: string;
  defaultEmail?: string;
  initial?: PlayerDetails | null;
  onSubmit: (details: PlayerDetails) => void;
}

export default function PlayerExtraDialog({
  open,
  onOpenChange,
  extra,
  teamLabel,
  roster,
  loadingRoster,
  rosterFailed,
  eventId,
  registrationId,
  allowFreeTextPlayer,
  requireHandles: requireHandlesProp = true,
  collectContact = false,
  defaultTeamName = "",
  defaultEmail = "",
  initial,
  onSubmit,
}: Props) {
  const isMobile = useIsMobile();
  const [name, setName] = useState("");
  const [jersey, setJersey] = useState("");
  const [ig, setIg] = useState("");
  const [x, setX] = useState("");
  const [teamName, setTeamName] = useState("");
  const [email, setEmail] = useState("");
  const [manual, setManual] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  // A team-level video product can still need handles; a player product always
  // needs the player identity. Fall back to the legacy prop when the extra
  // object doesn't carry the new booleans yet.
  const requiresPlayer = extra?.requires_player ?? (extra?.level === "player");
  const requireHandles = extra?.requires_handles ?? requireHandlesProp;

  // Self-fetch only when the caller didn't supply a roster.
  const fetched = useEventRosterPlayers(eventId, registrationId, roster === undefined && open);
  const list = roster ?? fetched.roster;
  const isLoading = roster !== undefined ? !!loadingRoster : fetched.loadingRoster;
  const failed = roster !== undefined ? !!rosterFailed : fetched.rosterFailed;

  /** No roster to pick from — a failed lookup, an empty roster, or a buyer with
   *  no registration context at all. Type it in rather than stall the sale. */
  const noRoster = !isLoading && (failed || list.length === 0);
  const manualMode = manual || noRoster;

  useEffect(() => {
    if (!open) return;
    setName(initial?.player_name || "");
    setJersey(initial?.player_jersey_number || "");
    setIg(cleanHandleInput(initial?.player_instagram_handle));
    setX(cleanHandleInput(initial?.player_x_handle));
    setTeamName(initial?.team_name || defaultTeamName || "");
    setEmail(initial?.buyer_email || defaultEmail || "");
    setManual(false);
    setShowErrors(false);
  }, [open, initial, defaultTeamName, defaultEmail]);

  const wantContact = requiresPlayer && collectContact;

  const errors: Record<string, string> = {};
  if (requiresPlayer) {
    if (!name.trim()) errors.name = "Player name is required";
    if (!jersey.trim()) errors.jersey = "Jersey number is required";
    if (wantContact && !teamName.trim()) errors.teamName = "Team name is required";
    if (wantContact && !isEmail(email)) errors.email = "A valid email is required";
  }
  if (requireHandles) {
    if (!isValidHandle(ig)) errors.ig = "Instagram handle is required";
    if (!isValidHandle(x)) errors.x = "X (Twitter) handle is required";
  }
  const complete = Object.keys(errors).length === 0;

  const submit = () => {
    if (!complete) { setShowErrors(true); return; }
    onSubmit({
      ...(requiresPlayer ? {
        player_name: name.trim(),
        player_jersey_number: jersey.trim(),
      } : {}),
      ...(requireHandles ? {
        player_instagram_handle: cleanHandle(ig) ?? undefined,
        player_x_handle: cleanHandle(x) ?? undefined,
      } : {}),
      ...(wantContact ? {
        team_name: teamName.trim(),
        buyer_email: email.trim(),
      } : {}),
    });
    onOpenChange(false);
  };

  const title = extra ? `${extra.name} — ${money(extra.price)}` : "Player details";

  const body = (
    <div className="space-y-4">
      <PlayerDetailsFields
        name={name}
        jersey={jersey}
        ig={ig}
        x={x}
        teamName={teamName}
        email={email}
        roster={list}
        loadingRoster={isLoading}
        rosterFailed={failed}
        allowFreeTextPlayer={allowFreeTextPlayer}
        requiresPlayer={requiresPlayer}
        requireHandles={requireHandles}
        manual={manualMode}
        onManual={() => setManual(true)}
        collectContact={wantContact}
        onChange={(p) => {
          if (p.name !== undefined) setName(p.name);
          if (p.jersey !== undefined) setJersey(p.jersey);
          if (p.ig !== undefined) setIg(p.ig);
          if (p.x !== undefined) setX(p.x);
          if (p.teamName !== undefined) setTeamName(p.teamName);
          if (p.email !== undefined) setEmail(p.email);
        }}
      />
      {showErrors && !complete && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive space-y-0.5">
          {Object.values(errors).map((m) => <div key={m}>{m}</div>)}
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        The media team works from these — a highlight package can't be produced without them.
      </p>
    </div>
  );


  const actions = (
    <div className="flex gap-3">
      <Button variant="outline" className="flex-1 h-11" onClick={() => onOpenChange(false)}>
        Cancel
      </Button>
      {/* Enabled even when incomplete: the buyer should read which field is
          missing, not stare at a dead button. */}
      <Button className="flex-1 h-11" onClick={submit}>
        {initial ? "Save" : "Add to cart"}
      </Button>
    </div>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        {/* Bottom sheet: content scrolls, actions stay reachable above the keyboard. */}
        <SheetContent side="bottom" className="max-h-[92vh] rounded-t-2xl p-0 flex flex-col">
          <SheetHeader className="text-left px-4 pt-5 pb-3">
            <SheetTitle className="text-base">{title}</SheetTitle>
            <SheetDescription>{teamLabel}</SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-4 pb-4">{body}</div>
          <div className="border-t bg-background px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            {actions}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{teamLabel}</DialogDescription>
        </DialogHeader>
        {body}
        {actions}
      </DialogContent>
    </Dialog>
  );
}
